import * as React from 'react'
import { Eye } from 'lucide-react'
import type { EmailLogDto } from '@lib/dto/email-log.dto'
import type { EmailStatus } from '@lib/enums/email-status.enum'
import { useEventEmailLog } from '@/hooks/useEvents'
import { instantLabel } from '@/lib/instantLabel'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PreviewTray, PreviewTrayDocument } from '@/components/ui/preview-tray'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * What this system has sent to the event's client contacts.
 *
 * Here, on the Clients tab, because the question it answers is always asked
 * about a person: "did she get the invitation?" A global mail log would answer
 * it too, and would make somebody search for a name they are already looking at.
 *
 * Keyed on the address rather than the account, so a contact removed from the
 * event still shows what was sent while they were on it — usually the very case
 * being asked about.
 */

/** How a delivery state reads. Color is never the only carrier — the word is. */
function statusTone(status: EmailStatus): BadgeTone {
  if (status === 'delivered' || status === 'opened' || status === 'clicked') return 'success'
  if (status === 'bounced' || status === 'complained' || status === 'failed') return 'danger'
  if (status === 'skipped' || status === 'delayed') return 'warning'
  return 'neutral'
}

export function EventEmailLog({ eventId }: { eventId: string }) {
  const log = useEventEmailLog(eventId)
  const [previewing, setPreviewing] = React.useState<EmailLogDto | null>(null)
  const emails = log.data?.emails ?? []

  return (
    <>
      <Table heading="Email sent to these contacts">
        <TableHeader>
          <TableHead>Sent</TableHead>
          <TableHead>To</TableHead>
          <TableHead>Subject</TableHead>
          <TableHead>Status</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {emails.length === 0 ? (
            <TableEmptyRow colSpan={5}>
              {log.isPending ? 'Loading…' : null}
              {log.isError ? 'The email log could not be loaded.' : null}
              {!log.isPending && !log.isError ? 'Nothing has been sent to these contacts yet.' : null}
            </TableEmptyRow>
          ) : (
            emails.map((email) => (
              <TableRow key={email.id}>
                <TableCell className="whitespace-nowrap text-ink-muted">
                  {instantLabel(email.sentAt)}
                </TableCell>
                <TableCell className="max-w-[16rem] truncate">{email.to}</TableCell>
                <TableCell className="max-w-[22rem] truncate">{email.subject}</TableCell>
                <TableCell>
                  {/* The provider's own last word sits under the status, because
                      "bounced" without "mailbox full" sends somebody guessing. */}
                  <Tooltip content={email.error ?? email.lastEvent ?? ''}>
                    <Badge tone={statusTone(email.status)} dot>
                      {email.status}
                    </Badge>
                  </Tooltip>
                </TableCell>
                <TableActionsCell>
                  {email.html === null ? (
                    // Sent before the log kept bodies, failed before it was
                    // rendered, or also about another organization's events.
                    // Said rather than shown as a control that does nothing.
                    <span className="text-xs text-ink-muted">{email.withheld ? 'also lists events elsewhere' : 'no copy'}</span>
                  ) : (
                    <Tooltip content="Read this email">
                      <button
                        type="button"
                        aria-label={`Read the email “${email.subject}” sent to ${email.to}`}
                        className="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-surface-hover hover:text-ink focus-visible:focus-ds"
                        onClick={() => {
                          setPreviewing(email)
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </Tooltip>
                  )}
                </TableActionsCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <PreviewTray
        open={previewing !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null)
        }}
        title={previewing?.subject ?? ''}
        description={previewing === null ? null : `To ${previewing.to} · ${instantLabel(previewing.sentAt)}`}
        meta={
          previewing === null ? null : (
            <span>
              From {previewing.from} · {previewing.status}
              {previewing.lastEvent === null ? '' : ` · ${previewing.lastEvent}`}
              {previewing.error === null ? '' : ` · ${previewing.error}`}
              {/* Said in the tray rather than left as a surprise: the stored
                  copy has its links defused, and somebody comparing it against
                  what a client forwarded them needs to know why they differ. */}
              <span className="ml-2 opacity-80">
                — sign-in links are removed from the stored copy
              </span>
            </span>
          )
        }
      >
        {previewing?.html == null ? null : (
          <PreviewTrayDocument html={previewing.html} title={`Email: ${previewing.subject}`} />
        )}
      </PreviewTray>
    </>
  )
}
